import React, { useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Button, Checkbox, Form, PageHeader, Popconfirm, Spin } from "antd";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import copy from 'copy-to-clipboard';
import _ from "lodash";
import moment from 'moment';

import { cancelPayment, fetchPayment, fetchPublicPayment, forwardPayment, generatePublicIdPayment, sendNotification } from "../../../redux/actions";
import { companyAddressLines, formatAddress, primaryAddress } from "../../../helpers/address";
import Modal from "antd/lib/modal/Modal";

export default ({ publicPayment = false }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [forwardModalVisible, setForwardModalVisible] = useState(false);

  const [forwardForm] = Form.useForm();

  const config = useSelector((state) => state.config, shallowEqual);
  const [payment, setPayment] = useState(null);

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const renderDate = (date) => moment(date).format('DD/MM/YYYY');

  useEffect(() => {
    (publicPayment ? dispatch(fetchPublicPayment(id)) : dispatch(fetchPayment(id)))
      .then(({ payload }) => {
        setPayment(payload);
        setLoading(false);
      });
  }, []);

  if (loading) return <Spin style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }} size="large" />;

  const onCancel = () => {
    setCancelling(true);
    dispatch(cancelPayment(id))
      .then(() => history.push("/payments"))
      .catch(() => setCancelling(false));
  };

  const onForward = async () => {
    await forwardForm.validateFields();
    const { emails } = forwardForm.getFieldsValue();
    setForwarding(true);
    dispatch(forwardPayment(id, emails))
      .finally(() => {
        setForwarding(false);
        onCancelModal();
      });
  };

  const onCancelModal = () => {
    setForwardModalVisible(false);
    forwardForm.resetFields();
  };

  const generatePublicId = () => {
    dispatch(generatePublicIdPayment(id)).then((res) => setPayment({ ...payment, publicId: res.payload }));
  }

  return (
    <div className="page-wrapper">
      <Modal
        title={intl.formatMessage({ id: 'forward' })}
        visible={forwardModalVisible}
        onOk={onForward}
        onCancel={onCancelModal}
        okText={intl.formatMessage({ id: "yes" })}
        cancelText={intl.formatMessage({ id: "no" })}
      >
        <Form form={forwardForm}>
          <Form.Item name="emails" rules={[{ required: true, message: intl.formatMessage({ id: "fieldRequired" }) }]}>
            <Checkbox.Group>
              {payment.client.emails.map(e => (
                <div key={e._id}>
                  <Checkbox value={e.emailAddress}>
                    {e.emailAddress} ({e.emailType})
                  </Checkbox>
                </div>
              ))}
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
      <PageHeader
        className="site-page-header-ghost-wrapper"
        ghost={false}
        title={intl.formatMessage({ id: "viewPayment" })}
        extra={[
          !publicPayment && !payment.publicId && <Button
            key="generatePublicId"
            type="primary"
            onClick={generatePublicId}
          >
            {intl.formatMessage({ id: "generatePublicId" })}
          </Button>,
          !publicPayment && payment.publicId && <Button
            key="copyLink"
            type="primary"
            onClick={() => {
              copy(`https://dnacleaning.com.cy/payments/public/${payment.publicId}`);
              dispatch(sendNotification({
                type: 'success',
                message: "copied",
                description: "linkCopiedToClipboard"
              }))
            }}
          >
            {intl.formatMessage({ id: "copyLink" })}
          </Button>,
          !publicPayment && <Button
            key="forward"
            type="primary"
            disabled={forwarding || payment.status === 'cancelled'}
            onClick={() => setForwardModalVisible(true)}
          >
            {intl.formatMessage({ id: "forward" })}
          </Button>,
          <Button
            key="print"
            type="primary"
            onClick={handlePrint}
          >
            {intl.formatMessage({ id: "print" })}
          </Button>,
          !publicPayment && (<Popconfirm
            key="delete"
            title={intl.formatMessage({ id: "cancelConfirmation" })}
            onConfirm={onCancel}
            onCancel={() => null}
            okText={intl.formatMessage({ id: "yes" })}
            cancelText={intl.formatMessage({ id: "no" })}
            disabled={cancelling || (payment.status === 'cancelled')}
          >
            <Button
              key="cancel"
              type="danger"
              disabled={cancelling || (payment.status === 'cancelled')}
            >
              {intl.formatMessage({ id: "cancel" })}
            </Button>
          </Popconfirm>)
        ]}
      />
      <div className="invoice invoice-sheet" style={{ border: "1px solid #f5f5f5" }} ref={componentRef}>
        <div className="invoice-main">
        <div className="invoice-header">
        <div className="invoice-top">
          <img src="/images/dna.png" alt="logo" />
          <div className="invoice-title">
            <strong>{intl.formatMessage({ id: "paymentReceipt" })}</strong>
            <div>{intl.formatMessage({ id: "paymentNum" })}: {_.get(payment, "_ref")}</div>
            <div>{intl.formatMessage({ id: "amount" })}: €{_.get(payment, "amount", 0).toFixed(2)}</div>
            <div>{intl.formatMessage({ id: "paymentMethod" })}: {intl.formatMessage({ id: payment?.method })}</div>
            <div>{intl.formatMessage({ id: "date" })}: {renderDate(_.get(payment, "transactionDate"))}</div>
          </div>
        </div>
        <div className="invoice-parties">
          <div>
            <div className="invoice-label">{intl.formatMessage({ id: "recipient" })}</div>
            <strong>{config.companyName}</strong>
            {companyAddressLines(config.address).map((line) => (
              <div key={line}>{line}</div>
            ))}
            <div>{intl.formatMessage({ id: "phoneSmall" })}: {config.phone}</div>
            <div>{intl.formatMessage({ id: "vat" })}: {config.taxID}</div>
            <div>{config.emailAddress}</div>
          </div>
          <div>
            <div className="invoice-label">{intl.formatMessage({ id: "from" })}</div>
            <strong>
              {_.get(payment, "client.companyName")
                || `${_.get(payment, "client.firstName", "")} ${_.get(payment, "client.lastName", "")}`.trim()}
            </strong>
            <div>{formatAddress(primaryAddress(payment.client))}</div>
            <div>
              {intl.formatMessage({ id: "name" })}:{" "}
              {_.get(payment, "client.firstName", "")} {_.get(payment, "client.lastName", "")}
            </div>
            <div>
              {intl.formatMessage({ id: "phoneSmall" })}:{" "}
              {_.get(payment, "client.phones", [])
                .filter((p) => p.phoneType !== "Fax")
                .map((p) => p.phoneNumber)
                .join(", ")}
            </div>
            <div>
              {_.get(payment, "client.emails", []).map((e) => e.emailAddress).join(", ")}
            </div>
          </div>
        </div>
        </div>
        <table className="invoice-lines">
          <thead>
            <tr>
              <th>{intl.formatMessage({ id: "paymentFor" })}</th>
              <th>{intl.formatMessage({ id: "invoiceDate" })}</th>
              <th className="num">{intl.formatMessage({ id: "invoiceTotal" })}</th>
              <th className="num">{intl.formatMessage({ id: "paymentAmount" })}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{intl.formatMessage({ id: "invoice" })} #{_.get(payment, "invoice._ref")}</td>
              <td>{renderDate(_.get(payment, "invoice.date"))}</td>
              <td className="num">€{_.get(payment, "invoice.breakdown.total", 0).toFixed(2)}</td>
              <td className="num">€{_.get(payment, "amount", 0).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        </div>
        <div className="invoice-footer">
          <div className="invoice-label">{intl.formatMessage({ id: "notes" })}</div>
          <div>{_.get(payment, "notes", "")}</div>
        </div>
      </div>
    </div>
  );
};
